import dayjs from "dayjs";
import FormatDate from '@utils/dateFormat';
import { additionalColumnsFormoduleGrid, color_codes, DATE_TIME_FORMATS, logedInUserLevelType } from "@constants/appConstants";
import { COMMON_CONSTANTS, dateFormate } from "@constants/index";
import { ancPeriodDateCalculation } from "@pages/Beneficiary-block/WomenComponent/ANC/Forms/CreateAndEdit/AncDateCaluculation";

interface HBYCDetail {
  hbyc: number;
  start: number;
  end: number;
};

interface HBYCResult {
  hbyc: number;
  startDate: string;
  endDate: string;
};
/**
 * Validates if the given value is a valid mobile number starting with 6-9 and exactly 10 digits long.
*/
const isValidMobileNumber = (value: any) => {
    return /^[6-9]\d*$/.test(value);
};
  
/**
 * Validates if the given value contains only alphabets and spaces.
*/
const isValidAlphabeticText = (value: any) => {
    return /^[a-zA-Z\s]*$/.test(value);
};

/**
 * function to adding dynamic colums in the grid.
*/
const handleDynamicColumnForGird = (userType: any, columnheader: any, addingColumsBeforWhichColumn: any, page: any) => {
  const modifiedColumnHeaders: any[] = JSON.parse(JSON.stringify(columnheader));
  const insertIndex = modifiedColumnHeaders.findIndex((ele) => ele.key === addingColumsBeforWhichColumn);
  if (userType === logedInUserLevelType.NationalLevelUser) {
    modifiedColumnHeaders.splice(insertIndex, 0, ...additionalColumnsFormoduleGrid.nationalLevel);
  } else if (userType === logedInUserLevelType.StateLevelUser) {
    modifiedColumnHeaders.splice(insertIndex, 0, ...additionalColumnsFormoduleGrid.stateLevel);
  } else if (userType === logedInUserLevelType.DistrictLevelUser) {
    modifiedColumnHeaders.splice(insertIndex, 0, ...additionalColumnsFormoduleGrid.districtLevel);
  } else if (userType === logedInUserLevelType.SubDistrictLevelUser) {
    const subDistColumn: any[] = page === additionalColumnsFormoduleGrid.pages.user_management ? additionalColumnsFormoduleGrid.subDistrictLevel.filter((ele: any) => ele.key !== 'ownership') : additionalColumnsFormoduleGrid.subDistrictLevel;
    modifiedColumnHeaders.splice(insertIndex, 0, ...subDistColumn);
  }

  return modifiedColumnHeaders;
};
/**
 * ======================================================================================
*/

/**
 * Adds a specified number of days to a given date.
 * @param {any} date - The initial date.
 * @param {number} noOfDaysToAdd - The number of days to add.
 * @param {any} unit - The unit of time to add (e.g., 'day', 'month', 'year').
 * @returns {any} - The new date after adding the specified number of days.
 */
const addDaysToDate = (date:any, noOfDaysToAdd:number, unit:any) => {
    return dayjs(date).add(noOfDaysToAdd, unit);
};

export function getDifferenceInDays(date1: Date, date2: Date): number {
  return Math.floor((date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Subtracts a specified number of days from a given date, including the current day.
 * @param {any} date - The initial date.
 * @param {number} noOfDaysToSubtract - The number of days to subtract.
 * @param {any} unit - The unit of time to add (e.g., 'day', 'month', 'year').
 * @returns {any} - The new date after subtracting the specified number of days.
 */
const subtractDaysFromDate = (date:any, noOfDaysToSubtract:number, unit:any) => {
    return dayjs(date).subtract(noOfDaysToSubtract - 1, unit);
};

/**
 * Compares two dates and returns true if they are the same day, false otherwise.
 * @param {any} date1 - The first date to compare.
 * @param {any} date2 - The second date to compare.
 * @param {any} unit - The unit of time to add (e.g., 'day', 'days', 'month').
 * @returns {boolean} - True if the dates are the same day, false otherwise.
 */
const areDatesSame = (date1:any, date2:any, unit:any) => {
    const isValidDate1 = dayjs(date1).isValid();
    const isValidDate2 = dayjs(date2).isValid();

    if (!isValidDate1 || !isValidDate2) {
        return false;
    } else {
        return dayjs(date1).isSame(dayjs(date2), unit);
    }
};

/**
 * Checks if a given value contains only digits.
 * @param {any} value - The value to check.
 * @returns {boolean} - True if the value contains only digits, false otherwise.
 */
const isValueOnlyDigits = (value: any) => {
    return /^\d+$/.test(value);
};
 
const getOngoingANCNumber = (lmpDate: string, visitDate?:any): any => {
    const ancDetails: any = [
      { anc: 1, start: 0, end: 91 },
      { anc: 2, start: 92, end: 189 },
      { anc: 3, start: 190, end: 245 },
      { anc: 4, start: 246, end: 280 },
    ];
  
    const currentDate = visitDate ? dayjs(visitDate) : dayjs();
    const newLmpDate = dayjs(lmpDate).isValid()
      ? dayjs(lmpDate)
      : dayjs();
  
    const results = ancDetails.map((anc: any) => {
      const startDate = FormatDate(newLmpDate.add(anc.start, 'day'))
      const endDate = FormatDate(newLmpDate.add(anc.end, 'day'));
      return {
        anc: anc.anc,
        startDate,
        endDate,
      };
    });
  
    const ongoingANC = results.filter((anc: any) => {
      const start = dayjs(anc.startDate);
      const end = dayjs(anc.endDate);
      return (
        (currentDate.isAfter(start) || currentDate.isSame(start, 'day')) &&
        (currentDate.isBefore(end) || currentDate.isSame(end, 'day'))
      );
    });
  
    return ongoingANC?.length > 0 ? ongoingANC[0] : {};
  };

  /**
   * Calculates weight-for-length zone classification (Red/Yellow/Green) based on WHO growth standards.
   * Uses API data fetched from backend containing WHO RBSK reference values.
   * @param {string} weight - Weight in kg
   * @param {string} length - Length/Height in cm
   * @param {string} gender - Gender ('Male' or 'Female')
   * @param {any} whoData - WHO reference data from API
   * @returns {string | null} - Color code for zone classification or null if invalid
   */
  export const feildValidation = (weight: string, length: string, gender: string, whoData: any): any => {
    const zoneDetails: any = [color_codes.red, color_codes.yellow, color_codes.green];
    const heightValue = parseFloat(length);
    const weightValue = parseFloat(weight);

    // Validate input
    if (isNaN(heightValue) || isNaN(weightValue) || heightValue <= 0 || weightValue <= 0) {
      return null;
    }

    // Validate WHO data is available
    if (!whoData || !whoData.boys || !whoData.girls) {
      return null;
    }

    if (heightValue < 45) {
      return zoneDetails[0]; // Below minimum - red
    }

    if (heightValue > 120) {
      return zoneDetails[2]; // Above maximum - green
    }

    const isMale = gender === color_codes.male;
    const dataSet = isMale ? whoData.boys : whoData.girls;

    // Linear interpolation helper
    const interpolate = (x: number, x0: number, x1: number, y0: number, y1: number): number => {
      return y0 + ((x - x0) * (y1 - y0)) / (x1 - x0);
    };

    let minus3SD: number;
    let minus2SD: number;
    let median: number;

    const heightKey = Math.round(heightValue).toString();
    
    if (dataSet[heightKey]) {
      // Exact match found
      minus3SD = dataSet[heightKey].minus3SD;
      minus2SD = dataSet[heightKey].minus2SD;
      median = dataSet[heightKey].median;
    } else {
      // Need to interpolate
      const lengths = Object.keys(dataSet).map(Number).sort((a, b) => a - b);
      let lowerLength = 0;
      let upperLength = 0;

      for (let i = 0; i < lengths.length - 1; i++) {
        if (heightValue > lengths[i] && heightValue < lengths[i + 1]) {
          lowerLength = lengths[i];
          upperLength = lengths[i + 1];
          break;
        }
      }

      if (lowerLength && upperLength) {
        const lowerData = dataSet[lowerLength.toString()];
        const upperData = dataSet[upperLength.toString()];
          
        minus3SD = interpolate(heightValue, lowerLength, upperLength, lowerData.minus3SD, upperData.minus3SD);
        minus2SD = interpolate(heightValue, lowerLength, upperLength, lowerData.minus2SD, upperData.minus2SD);
        median = interpolate(heightValue, lowerLength, upperLength, lowerData.median, upperData.median);
        
      } else {
        return null; // Out of range
      }
    }

    // Classify weight into zones based on WHO standards
    let result;
    if (weightValue < minus2SD) {
      result = zoneDetails[0];
    } else if (weightValue >= minus2SD && weightValue < median) {
      result = zoneDetails[1];
    } else {
      result = zoneDetails[2];
    }
    
    return result;
  }

/**
 * Checks if the service is executed through a VHND session and retrieves session details.
 * @param {any} eventExution - The event execution object containing session details.
 * @returns {object | null} - An object with sessionId and executionId if the service is from VHND, otherwise null.
*/  
const isServiceThroughSession = (eventExution:any) => {
  if(eventExution?.goToServices?.isFromVhnd) {
    return {
      sessionId: eventExution?.sessionDetailData?.session_id,
      executionId: eventExution?.sessionDetailData?.vhnd_execution_id
    };
  }
  return null;
}

const getVisitDateThroughVHND = (eventExution:any) => {
  if(eventExution?.goToServices?.isFromVhnd) {
    return dayjs();
  }
  return null;
}

const formateDateToTimeStamp = (date:any, time:any) => {
  const formattedDate = dayjs(date).format(DATE_TIME_FORMATS.DATE);
  const formattedTime = dayjs(time).format(DATE_TIME_FORMATS.TIME);
  
  const combinedDateTime = `${formattedDate} ${formattedTime}`;
  const timestampWithoutTimeZone = dayjs(combinedDateTime).format(DATE_TIME_FORMATS.DATE_TIME);
  
  return timestampWithoutTimeZone;
}

export { isValidMobileNumber, isValidAlphabeticText, handleDynamicColumnForGird, addDaysToDate, subtractDaysFromDate, isValueOnlyDigits, areDatesSame , getOngoingANCNumber, isServiceThroughSession, getVisitDateThroughVHND, formateDateToTimeStamp};
export const gender={
  male:'Male',
  female:'Female'
}

export const AddDaystoDate=(dbDateValue:any,days:number,format:string)=>{
  return dayjs(dbDateValue).add(days, 'day').format(format);
}

export const desiredFormatDate=(dbDateValue:any,format:string)=>{
  return dayjs(dbDateValue).format(format);
}

export const diffInYears = (date: string) => {
  const msInYear = 365.25 * 24 * 60 * 60 * 1000; 
  const currentDate = new Date();
  const [day, month, year] = date.split('-');
  const formattedDate = `${year}-${month}-${day}`;
  const parsedDate = new Date(formattedDate);
  const diffInMs = Math.abs(currentDate.getTime() - parsedDate.getTime());
  const diffInYears = Math.floor(diffInMs / msInYear);
  return diffInYears;
};


export const getHBYCPeriodDetails = (dob:any): HBYCResult[] => {
    const HBYCDetails: HBYCDetail[] = [
        { hbyc: 1, start: 90, end: 179 },
        { hbyc: 2, start: 180, end: 269 },
        { hbyc: 3, start: 270, end: 359 },
        { hbyc: 4, start: 360, end: 463 },
    ];
  
    const dateOfBirth = dayjs(dob);  
    const results = HBYCDetails.map(hbyc => {
        const startDate = FormatDate(dateOfBirth.add(hbyc.start, 'day'));
        const endDate = FormatDate(dateOfBirth.add(hbyc.end, 'day'));
        return {
            hbyc: hbyc.hbyc,
            startDate,
            endDate
        };
    });
  
    return results;
}

export const getMaturity= (lmpDate:string, deliveryDate:string)=>{

  const date1Ms = new Date(lmpDate).getTime()
  const date2Ms = new Date(deliveryDate).getTime()

  const dateDiff = date2Ms - date1Ms;
  const dayInMs = 1000*60*60*24;

  const days = dateDiff / dayInMs;

  if(days>=259 && days<294){
    return 'Full Term'
  }else if(days<259){
    return 'Preterm'
  }else{
    return ''
  }
}

export const formatDateBasedOnTimeZone = (date: any) => {
  const dateObject = new Date(date);
  const options: any = { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' };
  const formattedDate = dateObject.toLocaleString('en-GB', options).split('/').reverse().join('-');
  return dayjs(formattedDate).format(dateFormate.dd_mm_yyyy)
}
export const getDaysDifferenceBetweenDays = (date1:any, date2:any) => {
    const formattedDate1 = dayjs(date1);
    const formattedDate2 = dayjs(date2);

    return formattedDate2.diff(formattedDate1, 'days'); // to calculate the service over due date passed from the todays date
}

/**
 * Calculates the difference between two dates in the specified unit (e.g., days, months, years).
 * @param {any} date1 - The first date.
 * @param {any} date2 - The second date.
 * @param {string} unit - The unit of time to calculate the difference (e.g., 'days', 'months', 'years').
 * @returns {number} - The difference between the two dates in the specified unit.
 */
export const diffBetweenTwoDays = (date1:any, date2:any, unit: any) => {
  const formattedDate1 = dayjs(date1);
  const formattedDate2 = dayjs(date2);


  return formattedDate2.diff(formattedDate1, unit);
}

/**
 * Formats the month count into a readable string with "Month" or "Months".
 * @param {number} monthCount - The number of months.
 * @returns {string} - A formatted string like "1 Month" or "2 Months".
 */
export const formatMonthText = (monthCount: number): string => {
  return `${monthCount} ${monthCount === 1 ? 'Month' : 'Months'}`;
};

/**
 * Converts a weight in grams to kilograms and formats it as a string.
 * @param {number} grams - The weight in grams.
 * @returns {string} - The weight in kilograms formatted as "X.XX kg".
 */
export const convertGramsToKilograms = (grams: number): string => {
  if (!grams) return 'NA';
  const kilograms = grams / 1000;
  return `${kilograms.toFixed(2)} kg`;
};

/**
 * Returns the provided value or a default value if the value is null or undefined.
 * @param {any} value - The value to check.
 * @param {string} defaultValue - The default value to return if the value is null or undefined.
 * @returns {string} - The value or the default value.
 */
export const getValueOrDefault = (value: any, defaultValue: string = '-'): string => {
  return value ? value : defaultValue;
};

/**
 * Checks if the given value is a valid number (integer or decimal).
 * @param {any} value - The value to check.
 * @returns {boolean} - True if the value is a valid number, false otherwise.
 */
export const checkIsDigit = (value: any): boolean => {
  return /^(\d+(\.\d*)?|\.\d+)$/.test(value);
};

export const formatDate = (date: Date) => {
  return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}-${date.getFullYear()}`;
};

export const parseDurationValue = (durationLabel: string): { year: number; months: number[] } => {
  const monthMap: { [key: string]: number } = {
    January: 1, February: 2, March: 3, April: 4,
    May: 5, June: 6, July: 7, August: 8,
    September: 9, October: 10, November: 11, December: 12,
    Jan: 1, Feb: 2, Mar: 3, Apr: 4, Jun: 6, Jul: 7, Aug: 8,
    Sep: 9, Oct: 10, Nov: 11, Dec: 12
  };

  const monthlyMatch = durationLabel.match(/^([A-Za-z]+),\s*(\d{4})$/);
  if (monthlyMatch) {
    const month = monthMap[monthlyMatch[1]];
    const year = parseInt(monthlyMatch[2], 10);
    return { year, months: [month] };
  }

  const quarterlyMatch = durationLabel.match(/- (.+), (\d{4})$/);
  if (quarterlyMatch) {
    const monthNames = quarterlyMatch[1]
      .replace(/ and /g, ', ')
      .split(', ')
      .map((m) => m.trim());
    const months = monthNames.map((name) => monthMap[name]);
    const year = parseInt(quarterlyMatch[2], 10);
    return { year, months };
  }

  return { year: new Date().getFullYear(), months: [] };
};

export const isMobilizerHavingValue = (value:any) => {
  return value === COMMON_CONSTANTS.NOT_AVAILABLE ? null : value;
}

/**
 * Validates if the given value is a number between 1 and 18 (inclusive),
 * allowing up to 2 decimal places. Returns true for valid input, false otherwise.
 * @param {string} value - The value to validate.
 * @returns {boolean} - True if valid, false otherwise.
 */
export const isValidOneToEighteenWithDecimal = (value: string): boolean => {
  if (value === '') return true;
  if (!REGEX.ONE_TO_EIGHTEEN_DECIMAL_REGEX.test(value)) return false;
  const num = Number(value);
  return num >= 1 && num <= 18;
};


export const REGEX = {
/**
 * Regex for validating a number between 1 and 18 (inclusive), allowing up to 2 decimal places.
 * Examples: 1, 1.2, 10.25, 18, 18.00
 */
  // ONE_TO_EIGHTEEN_DECIMAL_REGEX: /^([1-9]|1[0-7])(\.\d{0,2})?$|^18(\.0{0,2})?$/,
  ONE_TO_EIGHTEEN_DECIMAL_REGEX: /^([1-9]|1[0-7])(\.\d{1})?$|^18(\.0)?$/,
  TWO_TO_EIGHTEEN_DECIMAL_STRICT: /^([2-9]|1[0-8])(\.\d{1,2})?$|^18(\.0{1,2})?$/,
}


export const calculateANCPeriods = (lmpDate:any) => {
    const ancPeriods = {
        firstANC: ancPeriodDateCalculation(lmpDate, 1),
        secondANC: ancPeriodDateCalculation(lmpDate, 2),
        thirdANC: ancPeriodDateCalculation(lmpDate, 3),
        fourthANC: ancPeriodDateCalculation(lmpDate, 4)
    };

    return ancPeriods;
}

export const formatNumber = (num: string): string => {
  // Split the number into parts
  const part1 = num.slice(0, 2);
  const part2 = num.slice(2, 6);
  const part3 = num.slice(6, 10);
  const part4 = num.slice(10);

  // Join the parts with hyphens
  return `${part1}-${part2}-${part3}-${part4}`;
};

export const isValidDate = (dateStr: string): boolean => {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  return regex.test(dateStr);
};
/**
 * Converts a given number to its Roman numeral representation.
 * Only supports numbers up to 10 (I to X) as per the defined mapping.
 *
 * @param num - The number to convert.
 * @returns The Roman numeral as a string.
 */
 export function toRoman(num: number): string {
    const romanMap: { [key: number]: string } = {
      10: 'X', 9: 'IX', 5: 'V', 4: 'IV', 1: 'I'
    };
    let result = '';
    for (const value of Object.keys(romanMap).map(Number).sort((a, b) => b - a)) {
      while (num >= value) {
        result += romanMap[value];
        num -= value;
      }
    }
    return result;
}

export const getNotDoneOption = (value: string): string => {
  return value ? value : COMMON_CONSTANTS.NOT_DONE;
}

  // manage json for health facility
  export const manageHealthFacilityJson = async (payload: any, villagesPayLoad: any, wardsPayLoad: any, villagesCount: any, wardsCount: any) => {
    const mainPayload: any = JSON.parse(JSON.stringify(payload));
      mainPayload.payLoadForVilageAndWard = {
        villagesPayLoad: villagesPayLoad,
        wardsPayLoad: wardsPayLoad
      }
      let combinedChunks: any[] = [];
      let wardChunks: any[] = [];
      let villageChunks: any[] = [];

      const chunkArray = async(arr: any, size: any) => {
        const result = [];
        for (let i = 0; i < arr.length; i += size) {
        result.push(arr.slice(i, i + size));
        }
        return result;
      }

      const combineChunkArray = async() => {
        const maxChunks = Math.max(villageChunks.length, wardChunks.length);
        combinedChunks = [];
        for (let i = 0; i < maxChunks; i++) {
            combinedChunks.push({
            villageId: villageChunks[i] || [], // fallback to [] if missing
            wardId: wardChunks[i] || [] // fallback to [] if missing
            });
          }
      }

      if ((payload.villageIds.length > 0 && payload.villageIds.length === villagesCount)) {
        mainPayload.villageIds = (payload.villageIds.length > 0 && payload.villageIds.length === villagesCount) ? 'all' : payload.villageIds;
      }

      if ((payload.wardIds.length > 0 && payload.wardIds.length === wardsCount)) {
        mainPayload.wardIds = (payload.wardIds.length > 0 && payload.wardIds.length === wardsCount) ? 'all' : payload.wardIds;
      }

      if (((payload.villageIds.length > 0 && payload.villageIds.length > 50 && payload.villageIds.length === villagesCount) && (payload.wardIds.length > 0 && payload.wardIds.length <= 50))) {
          mainPayload.villageIds = 'all';
          mainPayload.wardIds = payload.wardIds;
      }

      if (((payload.wardIds.length > 0 && payload.wardIds.length > 50 && payload.wardIds.length === wardsCount) && (payload.villageIds.length > 0 && payload.villageIds.length <= 50))) {
        mainPayload.villageIds =  payload.villageIds;
        mainPayload.wardIds = 'all';
      }

      if ((payload.villageIds.length > 0 && payload.villageIds.length <= 50)) {
        mainPayload.villageIds =  payload.villageIds;
      }

      if ((payload.wardIds.length > 0 && payload.wardIds.length <= 50)) {
        mainPayload.wardIds = payload.wardIds;
      }

      if (((payload.villageIds.length > 0 && payload.villageIds.length > 50 && payload.villageIds.length === villagesCount) && (payload.wardIds.length > 0 && payload.wardIds.length > 50 && payload.wardIds.length !== wardsCount))) {
        mainPayload.villageIds = 'all';
        mainPayload.wardIds = payload.wardIds.slice(0, 50);
        wardChunks = await chunkArray(payload.wardIds.slice(50), 50);
        await combineChunkArray();
      }

      if (((payload.wardIds.length > 0 && payload.wardIds.length > 50 && payload.wardIds.length === wardsCount) && (payload.villageIds.length > 0 && payload.villageIds.length > 50 && payload.villageIds.length !== villagesCount))) {
        mainPayload.villageIds = payload.villageIds.slice(0, 50);
        mainPayload.wardIds = 'all';
        villageChunks = await chunkArray(payload.villageIds.slice(50), 50);
        await combineChunkArray();
      }

      if ((payload.villageIds.length > 0 && payload.villageIds.length > 50 && payload.villageIds.length !== villagesCount) || (payload.wardIds.length > 0 && payload.wardIds.length > 50 && payload.wardIds.length !== wardsCount)) {
        mainPayload.villageIds = payload.villageIds.slice(0, 50);
        mainPayload.wardIds = payload.wardIds.slice(0, 50);
        villageChunks = await chunkArray(payload.villageIds.slice(50), 50)
        wardChunks = await chunkArray(payload.wardIds.slice(50), 50);
        await combineChunkArray();
      }

      return {mainPayload: mainPayload, combinedChunks: combinedChunks};
  }
